import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackFeatureUsage, trackEvent } from '../utils/analytics';

const PlatformTour = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('english');

  // Determine target audience based on user role
  const getTargetAudience = () => {
    if (user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR')) {
      return 'COACH';
    } else if (user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER')) {
      return 'CUSTOMER';
    }
    return 'CUSTOMER'; // Default fallback
  };

  const targetAudience = getTargetAudience();

  useEffect(() => {
    fetchTrainingVideos();
    trackFeatureUsage('Platform Tour', 'view');
    trackEvent('platform_tour_accessed', {
      event_category: 'Training',
      event_label: targetAudience,
      user_role: targetAudience
    });
  }, [targetAudience]);

  const fetchTrainingVideos = async () => {
    try {
      const response = await axios.get(`/api/training-videos/audience/${targetAudience}`);
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch training videos:', error);
      setError('Failed to load training videos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const extractYouTubeVideoId = (url) => {
    try {
      if (url.includes('youtube.com/watch?v=')) {
        return url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        return url.split('embed/')[1].split('?')[0];
      }
    } catch (error) {
      console.error('Failed to extract YouTube video ID:', error);
    }
    return null;
  };

  const getVideoThumbnail = (videoUrl) => {
    const videoId = extractYouTubeVideoId(videoUrl);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  const handleVideoClick = (video) => {
    const videoUrl = language === 'hindi' && video.videoUrlHindi ? video.videoUrlHindi : video.videoUrl;
    const videoTitle = language === 'hindi' && video.titleHindi ? video.titleHindi : video.title;
    
    trackEvent('training_video_watched', {
      event_category: 'Training',
      event_label: videoTitle,
      target_audience: targetAudience,
      video_id: video.id,
      language: language
    });
    window.open(videoUrl, '_blank');
  };

  const getDisplayTitle = (video) => {
    return language === 'hindi' && video.titleHindi ? video.titleHindi : video.title;
  };

  const getDisplayDescription = (video) => {
    return language === 'hindi' && video.descriptionHindi ? video.descriptionHindi : video.description;
  };

  const getDisplayVideoUrl = (video) => {
    return language === 'hindi' && video.videoUrlHindi ? video.videoUrlHindi : video.videoUrl;
  };

  const hasHindiContent = (video) => {
    return video.titleHindi || video.descriptionHindi || video.videoUrlHindi;
  };

  const getPageTitle = () => {
    switch (targetAudience) {
      case 'COACH': return 'Platform Tour for Coaches';
      case 'CUSTOMER': return 'Platform Tour for Customers';
      default: return 'Platform Tour';
    }
  };

  const getPageDescription = () => {
    switch (targetAudience) {
      case 'COACH': return 'Learn how to use CalorieNSweat coaching features effectively';
      case 'CUSTOMER': return 'Learn how to navigate and use your CalorieNSweat customer dashboard';
      default: return 'Learn how to use the CalorieNSweat platform';
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading platform tour...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>{getPageTitle()}</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            {getPageDescription()}
          </div>
        </div>
        
        {/* Language Toggle */}
        {videos.some(video => hasHindiContent(video)) && (
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            alignItems: 'center',
            background: 'var(--white)',
            padding: '8px',
            borderRadius: '8px',
            border: '2px solid var(--gray-medium)'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--gray-dark)' }}>
              Language:
            </span>
            <button
              className={`btn ${language === 'english' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ 
                fontSize: '12px', 
                padding: '6px 12px',
                minWidth: '80px'
              }}
              onClick={() => setLanguage('english')}
            >
              🇬🇧 English
            </button>
            <button
              className={`btn ${language === 'hindi' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ 
                fontSize: '12px', 
                padding: '6px 12px',
                minWidth: '80px'
              }}
              onClick={() => setLanguage('hindi')}
            >
              🇮🇳 हिंदी
            </button>
          </div>
        )}
      </div>

      <div style={{
        marginBottom: '20px',
        padding: '15px',
        background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
        color: 'white',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
          🎓 Welcome to Your Training Library
        </div>
        <div style={{ fontSize: '14px', opacity: '0.9' }}>
          Watch these videos to master the CalorieNSweat platform
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            🎥 Training Videos ({videos.length} available)
          </h3>
          <button 
            className="btn btn-secondary"
            onClick={fetchTrainingVideos}
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>

        {videos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎥</div>
            <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
              No Training Videos Available
            </h4>
            <p>
              Training videos for {targetAudience.toLowerCase()} users will appear here once they are added by administrators.
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '25px'
          }}>
            {videos.map((video) => (
              <div 
                key={video.id}
                style={{
                  border: '1px solid var(--gray-medium)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: 'var(--white)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.03)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onClick={() => handleVideoClick(video)}
              >
                {/* Video Thumbnail */}
                <div style={{ 
                  position: 'relative', 
                  paddingBottom: '56.25%', 
                  height: 0,
                  background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)'
                }}>
                  {getVideoThumbnail(getDisplayVideoUrl(video)) ? (
                    <img 
                      src={getVideoThumbnail(getDisplayVideoUrl(video))}
                      alt="Video thumbnail"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: getVideoThumbnail(getDisplayVideoUrl(video)) ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                    fontSize: '48px',
                    color: 'white'
                  }}>
                    🎥
                  </div>
                  
                  {/* Play Button Overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80px',
                    height: '80px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    color: 'white',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.9)';
                    e.target.style.transform = 'translate(-50%, -50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                    e.target.style.transform = 'translate(-50%, -50%) scale(1)';
                  }}
                  >
                    ▶️
                  </div>
                  
                  {/* Training Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'var(--primary-blue)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '15px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}>
                    🎓 {language === 'hindi' ? 'प्रशिक्षण' : 'TRAINING'}
                  </div>
                  
                  {/* Language Badge */}
                  {hasHindiContent(video) && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      background: language === 'hindi' ? 'var(--success)' : 'var(--gray-dark)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}>
                      {language === 'hindi' ? '🇮🇳 हिंदी' : '🇬🇧 EN'}
                    </div>
                  )}
                </div>
                
                {/* Video Info */}
                <div style={{ padding: '20px' }}>
                  <div style={{ 
                    fontWeight: '600', 
                    marginBottom: '12px',
                    fontSize: '18px',
                    lineHeight: '1.4',
                    color: 'var(--dark-blue)'
                  }}>
                    {getDisplayTitle(video)}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: 'var(--gray-dark)',
                    marginBottom: '15px',
                    lineHeight: '1.6'
                  }}>
                    {getDisplayDescription(video)}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--gray-medium)',
                    marginBottom: '15px'
                  }}>
                    Added: {new Date(video.createdAt).toLocaleDateString()}
                  </div>
                  
                  {/* Watch Button */}
                  <div style={{ 
                    background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}>
                    🎥 {language === 'hindi' ? 'प्रशिक्षण वीडियो देखें' : 'Watch Training Video'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Help Section */}
        <div style={{
          marginTop: '30px',
          padding: '20px',
          background: 'var(--pale-blue)',
          borderRadius: '8px',
          border: '2px solid var(--primary-blue)'
        }}>
          <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
            💡 Need More Help?
          </h4>
          <div style={{ fontSize: '14px', color: 'var(--gray-dark)', lineHeight: '1.6' }}>
            <p style={{ marginBottom: '10px' }}>
              These training videos are designed to help you get the most out of CalorieNSweat. 
              Watch them to learn about features, best practices, and tips for success.
            </p>
            <p style={{ marginBottom: '0' }}>
              If you need additional support, contact your {targetAudience === 'CUSTOMER' ? 'coach' : 'administrator'} 
              or reach out to our support team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformTour;
